import { useCallback, useEffect, useMemo, useState } from 'react';

import { Form } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';

import { MaasDataDiskMode, getDataDiskMode, getVolumeMountPath } from '@/delivery-type-plugin/plugins';

import {
  APP_STORAGE_GROUPS_FIELD,
  type AppStorageGroup,
  MAX_APP_STORAGE_GROUPS,
  buildImagePersistentVolume,
  createUserCustomStorageGroup,
  isEphemeralStorageGroup,
  isImageBackfillGroup,
  parseAppStorageGroupsField,
  readAppStorageGroupsFromForm,
  syncAppStorageMountPathField,
  syncVolumeMountPath,
  writeAppStorageGroupsField,
} from '../headless/appStorageGroups';
import { DEFAULT_APP_DATA_MOUNT_PATH } from '../types';

const APP_STORAGE_CONFIGURED_FIELD = 'appStorageConfigured';

interface UseAppStorageStateOptions {
  form: FormInstance;
}

/** 应用实例存储状态：多组挂载路径 + 数据盘 */
export function useAppStorageState({ form }: UseAppStorageStateOptions) {
  const groupsJson = Form.useWatch(APP_STORAGE_GROUPS_FIELD, form) as string | undefined;

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectModalOpen, setSelectModalOpen] = useState(false);
  const [editingGroupIndex, setEditingGroupIndex] = useState<number | null>(null);
  const [editingVolume, setEditingVolume] = useState<Record<string, unknown>>();

  const groups = useMemo(() => readAppStorageGroupsFromForm(form), [form, groupsJson]);

  const syncAppStorageConfiguredField = useCallback(() => {
    const currentGroups = readAppStorageGroupsFromForm(form);
    if (currentGroups.length) {
      form.setFieldValue(APP_STORAGE_CONFIGURED_FIELD, '1');
    } else {
      form.setFieldValue(APP_STORAGE_CONFIGURED_FIELD, undefined);
    }
  }, [form]);

  useEffect(() => {
    syncAppStorageConfiguredField();
  }, [groupsJson, syncAppStorageConfiguredField]);

  const updateGroups = useCallback(
    (next: AppStorageGroup[]) => {
      writeAppStorageGroupsField(form, next);
      syncAppStorageMountPathField(form, next);
      syncAppStorageConfiguredField();
      void form.validate([APP_STORAGE_CONFIGURED_FIELD]);
    },
    [form, syncAppStorageConfiguredField],
  );

  const handleMountPathChange = useCallback(
    (index: number, mountPath: string) => {
      if (isImageBackfillGroup(groups[index])) return;
      const next = groups.map((group, i) => {
        if (i !== index) return group;
        const trimmed = mountPath.trim();
        const volume = group.volume ? syncVolumeMountPath(group.volume, trimmed || group.mountPath) : undefined;
        return { ...group, mountPath: trimmed || group.mountPath, ...(volume ? { volume } : {}) };
      });
      updateGroups(next);
    },
    [groups, updateGroups],
  );

  const handleSaveVolume = useCallback(
    (volume: Record<string, unknown>) => {
      if (editingGroupIndex == null) return;
      const mountPath =
        String(groups[editingGroupIndex]?.mountPath || '').trim() || getVolumeMountPath(volume) || DEFAULT_APP_DATA_MOUNT_PATH;
      const normalized = syncVolumeMountPath(volume, mountPath);
      const next = groups.map((group, i) => (i === editingGroupIndex ? { ...group, mountPath, volume: normalized } : group));
      updateGroups(next);
      setEditingVolume(undefined);
      setEditingGroupIndex(null);
    },
    [editingGroupIndex, groups, updateGroups],
  );

  const handleDeleteVolume = useCallback(
    (index: number) => {
      if (isEphemeralStorageGroup(groups[index])) return;
      const next = groups.map((group, i) => {
        if (i !== index) return group;
        if (isImageBackfillGroup(group) && group.imageVolumeKind === 'persistent') {
          return { ...group, volume: buildImagePersistentVolume(group.mountPath) };
        }
        return { ...group, volume: undefined };
      });
      updateGroups(next);
    },
    [groups, updateGroups],
  );

  const handleAddGroup = useCallback(() => {
    if (groups.length >= MAX_APP_STORAGE_GROUPS) return;
    updateGroups([...groups, createUserCustomStorageGroup()]);
  }, [groups, updateGroups]);

  const handleRemoveGroup = useCallback(
    (index: number) => {
      if (isImageBackfillGroup(groups[index])) return;
      updateGroups(groups.filter((_, i) => i !== index));
    },
    [groups, updateGroups],
  );

  const openCreateModal = useCallback(
    (index: number) => {
      setEditingGroupIndex(index);
      setEditingVolume(groups[index]?.volume);
      setCreateModalOpen(true);
    },
    [groups],
  );

  const openSelectModal = useCallback(
    (index: number) => {
      setEditingGroupIndex(index);
      setEditingVolume(groups[index]?.volume);
      setSelectModalOpen(true);
    },
    [groups],
  );

  const openEditModal = useCallback(
    (index: number) => {
      const volume = groups[index]?.volume;
      if (!volume) return;
      setEditingGroupIndex(index);
      setEditingVolume(volume);
      const mode = getDataDiskMode(volume);
      if (mode === MaasDataDiskMode.Create) {
        setCreateModalOpen(true);
      } else {
        setSelectModalOpen(true);
      }
    },
    [groups],
  );

  const closeCreateModal = useCallback(() => {
    setCreateModalOpen(false);
    setEditingVolume(undefined);
    setEditingGroupIndex(null);
  }, []);

  const closeSelectModal = useCallback(() => {
    setSelectModalOpen(false);
    setEditingVolume(undefined);
    setEditingGroupIndex(null);
  }, []);

  const editingGroup = editingGroupIndex != null ? groups[editingGroupIndex] : undefined;
  const defaultMountPath = editingGroup?.mountPath || DEFAULT_APP_DATA_MOUNT_PATH;

  return {
    groups,
    createModalOpen,
    selectModalOpen,
    editingVolume,
    editingGroupIndex,
    defaultMountPath,
    handleSaveVolume,
    handleDeleteVolume,
    handleMountPathChange,
    handleAddGroup,
    handleRemoveGroup,
    openCreateModal,
    openSelectModal,
    openEditModal,
    closeCreateModal,
    closeSelectModal,
  };
}

/** 初始化存储组字段：无数据时保持 0 项 */
export function initAppStorageGroupsField(form: FormInstance) {
  const current = parseAppStorageGroupsField(form.getFieldValue(APP_STORAGE_GROUPS_FIELD));
  writeAppStorageGroupsField(form, current);
  syncAppStorageMountPathField(form, current);
}
